import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Plane, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/utils";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("form"); // form, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const urlToken = searchParams.get("token");
    if (!urlToken) {
      setStatus("error");
      setErrorMessage("Invalid reset link. No token provided.");
    } else {
      setToken(urlToken);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await api.post(`/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setStatus("success");
      toast.success("Password reset successfully!");
    } catch (error) {
      setStatus("error");
      setErrorMessage(error.response?.data?.detail || "Failed to reset password. The link may be invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Plane className="h-8 w-8 text-primary" />
          <span className="font-heading font-semibold text-xl tracking-wide">
            Cuvano® FlightCore
          </span>
        </div>

        {/* Card */}
        <div className="bg-card/50 border border-border rounded-lg p-8">
          {status === "form" && (
            <>
              <div className="text-center mb-6">
                <h2 className="font-medium text-lg mb-1">Reset Password</h2>
                <p className="text-muted-foreground text-sm">Enter your new password below.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-sm font-medium">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="bg-background pr-10 h-10 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password" className="text-sm font-medium">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-background h-10 text-sm"
                  />
                </div>

                <Button type="submit" className="w-full h-10 text-sm font-medium" disabled={loading}>
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          )}

          {status === "success" && (
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="font-medium text-lg mb-2">Password Reset!</h2>
              <p className="text-muted-foreground text-sm mb-6">
                Your password has been successfully reset.
              </p>
              <Button onClick={() => navigate("/login")} className="w-full">
                Continue to Sign In
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="text-center">
              <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h2 className="font-medium text-lg mb-2">Reset Failed</h2>
              <p className="text-muted-foreground text-sm mb-6">{errorMessage}</p>
              <Button onClick={() => navigate("/login")} className="w-full">
                Go to Sign In
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-zinc-500 text-xs tracking-wider text-center mt-8">
          POWERED BY CUVANO®
        </p>
      </div>
    </div>
  );
}
